import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Popover,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Slider,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { colors, shadows } from "../source/source.styles";
import NoDataFound from "../../Reports/NoDataFound";
import {
  Search,
  SlidersHorizontal,
  Eye,
  User,
  Target,
  Download,
  MessageSquare,
} from "lucide-react";
import EditGoalsModal from "./EditGoalsModal";
import MessageAgentModal from "./MessageAgentModal";
import { getEmployeePerformanceApi } from "../../../api/reports.api";
import socket from "../../../utils/socket";
import dayjs from "dayjs";

const EmployeePerformanceTable = ({ onViewProfile, dateRange }) => {
  const theme = useTheme();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [selectedEmployeeIndex, setSelectedEmployeeIndex] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editGoalsModalOpen, setEditGoalsModalOpen] = useState(false);
  const [messageAgentModalOpen, setMessageAgentModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [employeeData, setEmployeeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Use a ref to track if socket has been initialized
  const socketInitialized = useRef(false);

  // Convert dateRange to stable strings for dependency tracking
  const dateRangeKey = useMemo(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      return `${dateRange.startDate.getTime()}-${dateRange.endDate.getTime()}`;
    }
    return "";
  }, [dateRange]);

  const [filters, setFilters] = useState({
    roles: {
      sales: false,
      support: false,
      consultant: false,
    },
    teamSelection: "",
    revenueRange: {
      min: "",
      max: "",
    },
    efficiencyScore: {
      min: 0,
      max: 100,
    },
  });

  const openFilter = Boolean(filterAnchorEl);
  const openActionMenu = Boolean(actionMenuAnchorEl);

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleActionMenuClick = (event, index) => {
    setActionMenuAnchorEl(event.currentTarget);
    setSelectedEmployeeIndex(index);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setSelectedEmployeeIndex(null);
  };

  const handleActionClick = (action) => {
    const employee = employeeData[selectedEmployeeIndex];

    if (action === "view-profile" && onViewProfile) {
      onViewProfile(employee);
    } else if (action === "edit-goals") {
      setSelectedEmployee(employee);
      setEditGoalsModalOpen(true);
    } else if (action === "message-agent") {
      setSelectedEmployee(employee);
      setMessageAgentModalOpen(true);
    } else {
      console.log(`Action: ${action}`, employee);
    }

    handleActionMenuClose();
  };

  const handleRoleFilterChange = (role) => {
    setFilters((prev) => ({
      ...prev,
      roles: {
        ...prev.roles,
        [role]: !prev.roles[role],
      },
    }));
  };

  const handleApplyFilters = () => {
    console.log("Applying filters:", filters);
    handleFilterClose();
  };

  const handleClearFilters = () => {
    setFilters({
      roles: {
        sales: false,
        support: false,
        consultant: false,
      },
      teamSelection: "",
      revenueRange: {
        min: "",
        max: "",
      },
      efficiencyScore: {
        min: 0,
        max: 100,
      },
    });
  };

  const handleEfficiencyScoreChange = (event, newValue) => {
    setFilters((prev) => ({
      ...prev,
      efficiencyScore: {
        min: newValue[0],
        max: newValue[1],
      },
    }));
  };

  // Fetch employee performance data from API
  const fetchEmployeePerformance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build params with date range if provided
      const params = {};
      if (dateRange?.startDate) {
        params.from = dayjs(dateRange.startDate).toISOString();
      }
      if (dateRange?.endDate) {
        params.to = dayjs(dateRange.endDate).toISOString();
      }

      const response = await getEmployeePerformanceApi(params);

      if (response?.data?.data?.employees) {
        // Transform API data to match component structure
        const transformedData = response.data.data.employees.map((emp) => ({
          employeeId: emp.employeeId,
          name: emp.name,
          email: emp.email,
          team: emp.team,
          avatar: null,
          leadsHandled: emp.leadsHandled,
          totalCalls: emp.totalCalls,
          convertedLeads: emp.convertedLeads,
          totalRevenue: `₹${emp.totalRevenue.toLocaleString()}`,
          efficiencyScore: Math.round(emp.efficiencyScore),
        }));
        setEmployeeData(transformedData);
      }
    } catch (error) {
      console.error("Failed to fetch employee performance:", error);
      setError("Failed to load employee data");
    } finally {
      setLoading(false);
    }
  }, [dateRangeKey]);

  // Fetch data when date range changes
  useEffect(() => {
    fetchEmployeePerformance();
  }, [fetchEmployeePerformance]);

  // Setup socket listeners only once
  useEffect(() => {
    if (socketInitialized.current) return;
    socketInitialized.current = true;

    socket.connect();

    // Listen for real-time updates - trigger refresh
    const handleUpdate = () => {
      setRefreshTrigger((prev) => prev + 1);
    };

    socket.on("employee:metrics-updated", handleUpdate);
    socket.on("lead-created", handleUpdate);
    socket.on("lead-status-updated", handleUpdate);

    return () => {
      socket.off("employee:metrics-updated", handleUpdate);
      socket.off("lead-created", handleUpdate);
      socket.off("lead-status-updated", handleUpdate);
    };
  }, []);

  // Filter employees based on search query
  const filteredEmployees = employeeData.filter((employee) =>
    employee.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getAvatarColor = () => {
    return theme.palette.primary.main;
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: colors.textPrimary,
          }}
        >
          Detailed Employee Performance
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder="Search team members..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} color={colors.textMuted} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 240,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
                "& fieldset": {
                  borderColor: colors.borderLight,
                },
                "&:hover fieldset": {
                  borderColor: colors.border,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1,
              },
            }}
          />
          <IconButton
            onClick={handleFilterClick}
            sx={{
              color: openFilter ? colors.primary : colors.textMuted,
              border: `1px solid ${openFilter ? colors.primary : colors.borderLight}`,
              borderRadius: 2,
              "&:hover": {
                bgcolor: colors.bgPrimary,
                borderColor: colors.border,
              },
            }}
          >
            <SlidersHorizontal size={16} />
          </IconButton>
        </Stack>
      </Stack>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: colors.textMuted,
                textTransform: "uppercase",
                borderBottom: `1px solid ${colors.borderLight}`,
                py: 1.5,
                bgcolor: colors.bgPrimary,
              }}
            >
              Agent Name
            </TableCell>
            <TableCell
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: colors.textMuted,
                textTransform: "uppercase",
                borderBottom: `1px solid ${colors.borderLight}`,
                py: 1.5,
                bgcolor: colors.bgPrimary,
              }}
            >
              Leads Handled
            </TableCell>
            <TableCell
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: colors.textMuted,
                textTransform: "uppercase",
                borderBottom: `1px solid ${colors.borderLight}`,
                py: 1.5,
                bgcolor: colors.bgPrimary,
              }}
            >
              Total Calls
            </TableCell>
            <TableCell
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: colors.textMuted,
                textTransform: "uppercase",
                borderBottom: `1px solid ${colors.borderLight}`,
                py: 1.5,
                bgcolor: colors.bgPrimary,
              }}
            >
              Converted Leads
            </TableCell>
            <TableCell
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: colors.textMuted,
                textTransform: "uppercase",
                borderBottom: `1px solid ${colors.borderLight}`,
                py: 1.5,
                bgcolor: colors.bgPrimary,
              }}
            >
              Total Revenue
            </TableCell>
            <TableCell
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: colors.textMuted,
                textTransform: "uppercase",
                borderBottom: `1px solid ${colors.borderLight}`,
                py: 1.5,
                bgcolor: colors.bgPrimary,
              }}
              align="center"
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredEmployees.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} sx={{ border: "none" }}>
                <NoDataFound />
              </TableCell>
            </TableRow>
          ) : (
            filteredEmployees.map((employee, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:hover": {
                    bgcolor: colors.bgPrimary,
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: colors.textPrimary,
                    borderBottom: `1px solid ${colors.borderLight}`,
                    py: 2.5,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: getAvatarColor(),
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {employee.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </Avatar>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: colors.textPrimary,
                      }}
                    >
                      {employee.name}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: colors.textPrimary,
                    borderBottom: `1px solid ${colors.borderLight}`,
                    py: 2.5,
                  }}
                >
                  {employee.leadsHandled}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: colors.textPrimary,
                    borderBottom: `1px solid ${colors.borderLight}`,
                    py: 2.5,
                  }}
                >
                  {employee.totalCalls}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: colors.success,
                    borderBottom: `1px solid ${colors.borderLight}`,
                    py: 2.5,
                  }}
                >
                  {employee.convertedLeads}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: colors.textPrimary,
                    borderBottom: `1px solid ${colors.borderLight}`,
                    py: 2.5,
                  }}
                >
                  {employee.totalRevenue}
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: `1px solid ${colors.borderLight}`,
                    py: 2.5,
                  }}
                  align="center"
                >
                  <IconButton
                    size="small"
                    onClick={(event) => handleActionMenuClick(event, index)}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": {
                        bgcolor: colors.bgPrimary,
                      },
                    }}
                  >
                    <Eye size={18} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: `1px solid ${colors.borderLight}`,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: colors.textSecondary,
          }}
        >
          Showing {filteredEmployees.length} of {employeeData.length} team
          performers by Efficiency Score
        </Typography>
      </Box>

      {/* Filter Popover */}
      <Popover
        open={openFilter}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
            width: 260,
          },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: colors.textPrimary,
              mb: 2,
            }}
          >
            Filter Employees
          </Typography>

          {/* Role Filter */}
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: colors.textMuted,
                letterSpacing: 0.5,
                mb: 1,
              }}
            >
              ROLE
            </Typography>
            <Stack spacing={0.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.roles.sales}
                    onChange={() => handleRoleFilterChange("sales")}
                    size="small"
                    sx={{
                      color: colors.borderLight,
                      "&.Mui-checked": {
                        color: colors.primary,
                      },
                      py: 0.5,
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>
                    Sales
                  </Typography>
                }
                sx={{ ml: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.roles.support}
                    onChange={() => handleRoleFilterChange("support")}
                    size="small"
                    sx={{
                      color: colors.borderLight,
                      "&.Mui-checked": {
                        color: colors.primary,
                      },
                      py: 0.5,
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>
                    Support
                  </Typography>
                }
                sx={{ ml: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.roles.consultant}
                    onChange={() => handleRoleFilterChange("consultant")}
                    size="small"
                    sx={{
                      color: colors.borderLight,
                      "&.Mui-checked": {
                        color: colors.primary,
                      },
                      py: 0.5,
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>
                    Consultant
                  </Typography>
                }
                sx={{ ml: 0 }}
              />
            </Stack>
          </Box>

          {/* Team Selection */}
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: colors.textMuted,
                letterSpacing: 0.5,
                mb: 1,
              }}
            >
              TEAM SELECTION
            </Typography>
            <TextField
              placeholder="Search team..."
              size="small"
              fullWidth
              value={filters.teamSelection}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  teamSelection: e.target.value,
                }))
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: 12,
                  borderRadius: 2,
                },
                "& .MuiOutlinedInput-input": {
                  py: 0.75,
                },
              }}
            />
          </Box>

          {/* Revenue Range */}
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: colors.textMuted,
                letterSpacing: 0.5,
                mb: 1,
              }}
            >
              REVENUE RANGE (₹)
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                placeholder="Min"
                size="small"
                value={filters.revenueRange.min}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    revenueRange: { ...prev.revenueRange, min: e.target.value },
                  }))
                }
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    fontSize: 12,
                    borderRadius: 2,
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 0.75,
                  },
                }}
              />
              <TextField
                placeholder="Max"
                size="small"
                value={filters.revenueRange.max}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    revenueRange: { ...prev.revenueRange, max: e.target.value },
                  }))
                }
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    fontSize: 12,
                    borderRadius: 2,
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 0.75,
                  },
                }}
              />
            </Stack>
          </Box>

          {/* Efficiency Score Slider */}
          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={0.5}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                EFFICIENCY SCORE
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.primary,
                }}
              >
                {filters.efficiencyScore.min} - {filters.efficiencyScore.max}
              </Typography>
            </Stack>
            <Slider
              value={[filters.efficiencyScore.min, filters.efficiencyScore.max]}
              onChange={handleEfficiencyScoreChange}
              valueLabelDisplay="auto"
              min={0}
              max={100}
              sx={{
                color: colors.primary,
                "& .MuiSlider-thumb": {
                  width: 16,
                  height: 16,
                },
                "& .MuiSlider-track": {
                  height: 4,
                },
                "& .MuiSlider-rail": {
                  height: 4,
                  opacity: 0.3,
                },
              }}
            />
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Action Buttons */}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              fullWidth
              sx={{
                py: 0.75,
                fontSize: 12,
                fontWeight: 600,
                textTransform: "none",
                borderColor: colors.border,
                color: colors.textPrimary,
                borderRadius: 2,
                "&:hover": {
                  borderColor: colors.border,
                  bgcolor: colors.bgPrimary,
                },
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              fullWidth
              sx={{
                py: 0.75,
                fontSize: 12,
                fontWeight: 600,
                textTransform: "none",
                bgcolor: theme.palette.primary.main,
                color: "#FFFFFF",
                borderRadius: 2,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: theme.palette.primary.dark,
                  boxShadow: "none",
                },
              }}
            >
              Apply Filters
            </Button>
          </Stack>
        </Box>
      </Popover>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={openActionMenu}
        onClose={handleActionMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            minWidth: 200,
          },
        }}
      >
        <MenuItem
          onClick={() => handleActionClick("view-profile")}
          sx={{
            py: 1.5,
            px: 2,
            fontSize: 13,
            fontWeight: 500,
            color: colors.textPrimary,
            "&:hover": {
              bgcolor: colors.bgPrimary,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <User size={18} color={colors.textSecondary} />
          </ListItemIcon>
          <ListItemText
            primary="View Full Profile"
            primaryTypographyProps={{
              fontSize: 13,
              fontWeight: 500,
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => handleActionClick("edit-goals")}
          sx={{
            py: 1.5,
            px: 2,
            fontSize: 13,
            fontWeight: 500,
            color: colors.textPrimary,
            "&:hover": {
              bgcolor: colors.bgPrimary,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Target size={18} color={colors.textSecondary} />
          </ListItemIcon>
          <ListItemText
            primary="Edit Individual Goals"
            primaryTypographyProps={{
              fontSize: 13,
              fontWeight: 500,
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => handleActionClick("export-data")}
          sx={{
            py: 1.5,
            px: 2,
            fontSize: 13,
            fontWeight: 500,
            color: colors.textPrimary,
            "&:hover": {
              bgcolor: colors.bgPrimary,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Download size={18} color={colors.textSecondary} />
          </ListItemIcon>
          <ListItemText
            primary="Export Performance Data"
            primaryTypographyProps={{
              fontSize: 13,
              fontWeight: 500,
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => handleActionClick("message-agent")}
          sx={{
            py: 1.5,
            px: 2,
            fontSize: 13,
            fontWeight: 500,
            color: colors.textPrimary,
            "&:hover": {
              bgcolor: colors.bgPrimary,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <MessageSquare size={18} color={colors.textSecondary} />
          </ListItemIcon>
          <ListItemText
            primary="Message Agent"
            primaryTypographyProps={{
              fontSize: 13,
              fontWeight: 500,
            }}
          />
        </MenuItem>
      </Menu>

      {/* Edit Goals Modal */}
      <EditGoalsModal
        open={editGoalsModalOpen}
        onClose={() => {
          setEditGoalsModalOpen(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
      />

      {/* Message Agent Modal */}
      <MessageAgentModal
        open={messageAgentModalOpen}
        onClose={() => {
          setMessageAgentModalOpen(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
      />
    </Box>
  );
};

export default EmployeePerformanceTable;
